import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MoreIcon } from "./Icons.tsx";

export type RowMenuItem = {
  label: string;
  onSelect: () => void;
  /** `danger` for an action that removes something, "Delete". It is red only inside the menu. */
  tone?: "default" | "danger";
};

/**
 * The "More" button at the end of a table row, with a short menu under it.
 *
 * For an action a person must not hit by mistake, such as Delete: it is one tap further away than
 * a button in the row, and it is not red until the menu is open. The action still asks first
 * (`ConfirmDialog`).
 *
 * The menu is `position: fixed` at the button, so the table's own scroll box does not cut it off.
 * It opens under the button, or above it when there is no room under it (the last rows).
 * It closes on a pick, on Escape, on a tap outside, and when the page scrolls or resizes (it would
 * no longer sit at its button). Clicks and keys stop at the button and the menu, so the row under
 * it does not open.
 */
export function RowMenu({ label, items }: { label: string; items: RowMenuItem[] }) {
  /** `below` and `above` are the button's bottom and top edges; `up` is set once the menu is measured. */
  const [at, setAt] = useState<{ below: number; above: number; right: number; up: boolean } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const open = at !== null;

  useLayoutEffect(() => {
    if (!at || at.up || !menu.current) return;
    const height = menu.current.offsetHeight;
    if (at.below + 4 + height > window.innerHeight - 8 && at.above - 4 - height >= 8) setAt({ ...at, up: true });
  }, [at]);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const close = () => setAt(null);
    // Scroll and resize close the menu under a keyboard user too: focus goes back to the button, not
    // to nowhere. A tap outside leaves focus where the tap put it.
    const drop = () => {
      const inside = menu.current?.contains(document.activeElement) ?? false;
      close();
      if (inside) button.current?.focus();
    };
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!menu.current?.contains(target) && !button.current?.contains(target)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      close();
      button.current?.focus();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", drop, true);
    window.addEventListener("resize", drop);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", drop, true);
      window.removeEventListener("resize", drop);
    };
  }, [open]);

  return (
    <span className="inline-flex">
      <button
        ref={button}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onKeyDown={(event) => event.stopPropagation()}
        // A press on the button of an open menu keeps focus where it is, so the menu's blur does not
        // close it first and the click below open it again. The click closes it.
        onMouseDown={(event) => {
          if (open) event.preventDefault();
        }}
        onClick={(event) => {
          event.stopPropagation();
          if (open) {
            // Safari gives no focus to the tapped button, and the focused item is about to go.
            button.current?.focus();
            return setAt(null);
          }
          const box = button.current?.getBoundingClientRect();
          if (box) setAt({ below: box.bottom, above: box.top, right: window.innerWidth - box.right, up: false });
        }}
        className="flex size-9 items-center justify-center rounded-lg text-ink-2 hover:bg-surface active:bg-sunken"
      >
        <MoreIcon size={18} />
      </button>
      {at ? (
        <div
          ref={menu}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          style={
            at.up
              ? { bottom: window.innerHeight - at.above + 4, right: at.right }
              : { top: at.below + 4, right: at.right }
          }
          onKeyDown={(event) => event.stopPropagation()}
          // Tab or Shift+Tab out of the menu (to the More button too) closes it, and the browser moves
          // focus on as usual. Only when focus goes
          // to a control: Safari gives no focus to a tapped button, so a tap on an item blurs it with
          // no next element, and closing then would drop the tap before its click.
          onBlur={(event) => {
            const next = event.relatedTarget as Node | null;
            if (next && !menu.current?.contains(next)) setAt(null);
          }}
          className="fixed z-40 min-w-40 rounded-card border border-line-strong bg-paper p-1 shadow-[0_12px_32px_-12px_rgba(17,26,43,0.35)]"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              onClick={(event) => {
                event.stopPropagation();
                // Focus goes to the More button first, so a dialog the item opens gives focus back to
                // a control that is still on the page, not to this item, which is gone.
                button.current?.focus();
                setAt(null);
                item.onSelect();
              }}
              className={[
                "flex w-full items-center rounded-lg px-3 py-2 text-left text-[14px] font-semibold",
                item.tone === "danger" ? "text-cancelled hover:bg-cancelled-surface" : "text-ink hover:bg-surface",
              ].join(" ")}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}
